import { useTranslations } from "@services/strings";
import { cn } from "@/ui/utils/cn";
import {
  ArrowUpRight,
  Bell,
  Grid2x2,
  HandCoins,
  House,
  Tag,
  CircleUserRound,
} from "lucide-react";

type Property = { name: string; developer: string };

/**
 * The MNZL app's home screen as a mock-up in markup, in place of the
 * screenshots the previous site used, so it localizes and stays in step with
 * the brand. Mirrors the Flex home: financing card, the two products, the
 * property with its value today and the next installment.
 */
export const AppHomeScreen = ({
  financing = 10_500_000,
  valueGrowth = 0.269,
  nextInstallment = 133_800,
  paid = 50,
  total = 74,
  animate = true,
  property,
}: {
  financing?: number;
  valueGrowth?: number;
  nextInstallment?: number;
  paid?: number;
  total?: number;
  animate?: boolean;
  /** The unit shown on the home card; defaults to the sample in the locale. */
  property?: Property;
}) => {
  const { t, formatNumber, formatPercent } = useTranslations();
  const unit: Property = property ?? {
    name: t("site.app.propertyName"),
    developer: t("site.app.developer"),
  };

  return (
    <div className="flex h-full flex-col bg-white px-4 pb-3 pt-12 text-ink">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] text-slate-500">{t("site.app.greeting")}</p>
          <p className="text-[17px] font-semibold leading-tight">
            {t("site.app.name")}
          </p>
        </div>
        <span className="grid h-8 w-8 place-items-center rounded-full bg-mist text-ink">
          <Bell size={14} />
        </span>
      </div>

      <div className="mt-3 rounded-2xl bg-navy p-4 text-white">
        <p className="text-[11px] text-slate-300">
          {t("site.app.financingUpTo")}
        </p>
        <p className="mt-1 flex items-baseline gap-1.5">
          <span className="text-[11px] font-medium text-slate-300">EGP</span>
          <span className="tabular text-[26px] font-bold leading-none tracking-tight">
            {formatNumber(financing)}
          </span>
        </p>
        <p className="mt-2 text-[10.5px] leading-snug text-slate-300">
          {t("site.app.hint")}
        </p>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <p className="text-[13px] font-semibold">{t("site.app.products")}</p>
        <p className="text-[10.5px] text-slate-500">{t("site.app.seeAll")} ›</p>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-slate-200 p-2.5">
          <div className="flex items-center gap-1.5">
            <span className="grid h-6 w-6 place-items-center rounded-md bg-mist">
              <House size={12} />
            </span>
            <span className="text-[12px] font-semibold">
              {t("site.app.unlock")}
            </span>
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-slate-500">
            {t("site.app.unlockDesc")}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 p-2.5">
          <div className="flex items-center gap-1.5">
            <span className="grid h-6 w-6 place-items-center rounded-md bg-lavender">
              <Tag size={12} />
            </span>
            <span className="text-[12px] font-semibold">
              {t("site.app.payLess")}
            </span>
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-slate-500">
            {t("site.app.payLessDesc")}
          </p>
        </div>
      </div>

      <p className="mt-3 text-[13px] font-semibold">{t("site.app.upcoming")}</p>
      <div className="mt-2 rounded-xl border border-slate-200 p-2.5">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-mist">
            <House size={15} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[12px] font-semibold">{unit.name}</p>
            <p className="truncate text-[10px] text-slate-500">
              {unit.developer}
            </p>
          </div>
        </div>
        <span
          className={cn(
            "mt-2 inline-flex items-center gap-1 rounded-full bg-mint-soft px-2 py-0.5 text-[10.5px] font-semibold text-mint-ink",
            animate && "animate-rise [animation-delay:900ms]",
          )}
        >
          <ArrowUpRight size={11} />
          {t("site.app.valueUp", { percent: formatPercent(valueGrowth) })}
        </span>
        <div className="mt-2 rounded-lg bg-mist px-2.5 py-2">
          <div className="flex justify-between text-[10px] text-slate-500">
            <span>{t("site.app.nextInstallment")}</span>
            <span className="tabular">
              {t("site.app.installments", { paid, total })}
            </span>
          </div>
          <div className="mt-0.5 flex justify-between text-[11px] font-semibold">
            <span className="tabular" dir="ltr">
              5 Sep 2026
            </span>
            <span className="tabular" dir="ltr">
              EGP {formatNumber(nextInstallment)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-auto flex justify-between border-t border-slate-100 px-2 pt-2 text-[9px] text-slate-400">
        <span className="flex flex-col items-center gap-0.5 text-ink">
          <House size={14} />
          {t("site.app.home")}
        </span>
        <span className="flex flex-col items-center gap-0.5">
          <Grid2x2 size={14} />
          {t("site.app.assets")}
        </span>
        <span className="flex flex-col items-center gap-0.5">
          <HandCoins size={14} />
          {t("site.app.loans")}
        </span>
        <span className="flex flex-col items-center gap-0.5">
          <CircleUserRound size={14} />
          {t("site.app.profile")}
        </span>
      </div>
    </div>
  );
};
